import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Heart, Shield, Sword, Dices, Award,
  BookOpen, Map, Coins, ChevronLeft,
  ScrollText, Star, Pencil, Sparkles
} from 'lucide-react';
import { gameRuleService } from '../services/gameRuleService';
import CharacterSkills from './CharacterSkills';
import './CharacterViewer.css';

const CharacterViewer = ({ character, onClose, onCharacterUpdate }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview'); // 'overview', 'skills', 'inventory'

  const getModifier = (score) => {
    const mod = gameRuleService.calculateModifier(score);
    return mod >= 0 ? `+${mod}` : mod;
  };

  const stats = useMemo(() => [
    { 
      icon: <Heart className="character-viewer__stat-icon" />, 
      label: 'Hit Points', 
      value: `${character.currentHealth}/${character.maxHealth}` 
    },
    { 
      icon: <Shield className="character-viewer__stat-icon" />, 
      label: 'Armor Class', 
      value: character.armorClass 
    },
    { 
      icon: <Sword className="character-viewer__stat-icon" />, 
      label: 'Initiative', 
      value: getModifier(character.dexterity) 
    },
    { 
      icon: <Star className="character-viewer__stat-icon" />, 
      label: 'Proficiency', 
      value: `+${character.proficiencyBonus}` 
    },
    { 
      icon: <Coins className="character-viewer__stat-icon" />, 
      label: 'Gold', 
      value: character.gold 
    }
  ], [character]);

  const abilities = useMemo(() => [
    { name: 'Strength', value: character.strength },
    { name: 'Dexterity', value: character.dexterity },
    { name: 'Constitution', value: character.constitution },
    { name: 'Intelligence', value: character.intelligence },
    { name: 'Wisdom', value: character.wisdom },
    { name: 'Charisma', value: character.charisma }
  ], [character]);

  // Render tab content based on active tab
  const renderTabContent = () => {
    switch (activeTab) {
      case 'skills':
        return (
          <CharacterSkills
            character={character}
            onCharacterUpdate={onCharacterUpdate}
          />
        );
      case 'inventory':
        // Inventory tab would go here when implemented
        return (
          <div className="character-viewer__placeholder">
            <p>Inventory management coming soon!</p>
          </div>
        );
      case 'overview':
      default:
        return (
          <>
            <div className="character-viewer__stats">
              {stats.map((stat, index) => (
                <div key={index} className="character-viewer__stat">
                  {stat.icon}
                  <span className="character-viewer__stat-value">{stat.value}</span>
                  <span className="character-viewer__stat-label">{stat.label}</span>
                </div>
              ))}
            </div>

            <section className="character-viewer__section">
              <h2 className="character-viewer__section-title">
                <ScrollText />
                Abilities
              </h2>
              <div className="character-viewer__abilities-grid">
                {abilities.map((ability) => (
                  <div key={ability.name} className="character-viewer__ability">
                    <div className="character-viewer__ability-name">{ability.name}</div>
                    <div className="character-viewer__ability-score">{ability.value}</div>
                    <div className="character-viewer__ability-modifier">
                      {getModifier(ability.value)}
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {character.backstory && (
              <section className="character-viewer__section">
                <h2 className="character-viewer__section-title">
                  <BookOpen />
                  Backstory
                </h2>
                <div className="character-viewer__content">
                  <p className="character-viewer__content-text">{character.backstory}</p>
                </div>
              </section>
            )}

            {character.notes && (
              <section className="character-viewer__section">
                <h2 className="character-viewer__section-title">
                  <Map />
                  Notes
                </h2>
                <div className="character-viewer__content">
                  <p className="character-viewer__content-text">{character.notes}</p>
                </div>
              </section>
            )}
          </>
        );
    }
  };

  return (
    <div className="character-viewer">
      <header className="character-viewer__header">
        <button
          className="character-viewer__button character-viewer__button--back"
          onClick={onClose}
          aria-label="Back to character list"
        >
          <ChevronLeft />
          <span>Back</span>
        </button>

        <button
          className="character-viewer__button character-viewer__button--edit"
          onClick={() => navigate(`/edit-character/${character.id}`)}
          aria-label="Edit character"
        >
          <Pencil />
          <span>Edit Character</span>
        </button>
      </header>

      <div className="character-viewer__hero">
        <h1 className="character-viewer__name">{character.name}</h1>
        <div className="character-viewer__subtitle">
          Level {character.level} {character.race} {character.characterClass}
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="character-viewer__tabs">
        <button
          className={`character-viewer__tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          <ScrollText className="character-viewer__tab-icon" />
          Overview
        </button>
        <button
          className={`character-viewer__tab ${activeTab === 'skills' ? 'active' : ''}`}
          onClick={() => setActiveTab('skills')}
        >
          <Sparkles className="character-viewer__tab-icon" />
          Skills
        </button>
        <button
          className={`character-viewer__tab ${activeTab === 'inventory' ? 'active' : ''}`}
          onClick={() => setActiveTab('inventory')}
        >
          <Coins className="character-viewer__tab-icon" />
          Inventory
        </button>
      </div>

      {/* Content based on active tab */}
      <div className="character-viewer__tab-content">
        {renderTabContent()}
      </div>
    </div>
  );
};

export default CharacterViewer;